import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LogOut, ClipboardList, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PageHeading, LoadingBlock } from "@/components/states";
import { AddressSection } from "@/components/AddressCard";
import { PasswordSection } from "@/components/PasswordSection";
import { customerNumber } from "@/lib/product";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
  approved: "account.statusApproved",
  pending: "account.statusPending",
  rejected: "account.statusRejected",
};

const Account = () => {
  const { t } = useTranslation();
  const { account, logout, booting } = useAuth();
  const navigate = useNavigate();

  if (booting || !account) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <LoadingBlock />
      </div>
    );
  }

  const rows = [
    { label: t("account.customerNumber"), value: customerNumber(account) },
    { label: t("account.company"), value: account.bedrijfsnaam },
    {
      label: t("account.contact"),
      value: [account.voornaam, account.achternaam].filter(Boolean).join(" "),
    },
    { label: t("account.email"), value: account.email },
    { label: t("account.kvk"), value: account.organisatie?.kvk },
    { label: t("account.vatNumber"), value: account.organisatie?.btw_nummer },
  ].filter((r) => r.value);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8" data-testid="account-page">
      <PageHeading
        title={t("account.title")}
        subtitle={t("account.subtitle")}
        testId="account-heading"
      />

      <Card className="mt-6 p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-semibold">{account.bedrijfsnaam}</h2>
          {account.status && (
            <Badge
              variant="secondary"
              className="whitespace-normal"
              data-testid="account-status-badge"
            >
              {t(STATUS_LABEL[account.status] || "common.unknown")}
            </Badge>
          )}
        </div>

        <Separator className="my-5" />

        <dl className="space-y-3 text-sm">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-4">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="break-all font-medium sm:text-right">{row.value}</dd>
            </div>
          ))}
        </dl>

        <p className="mt-6 rounded-md border bg-secondary p-4 text-xs text-muted-foreground">
          {t("account.editNotice")}
        </p>
        <p
          className="mt-3 rounded-md border bg-secondary p-4 text-xs text-muted-foreground"
          data-testid="account-price-explainer"
        >
          {t("price.explainer")}
        </p>

        <Separator className="my-5" />

        <AddressSection />

        <Separator className="my-5" />

        <PasswordSection />

        <Separator className="my-5" />

        <p className="text-xs uppercase tracking-widest text-muted-foreground">
          {t("account.quickLinks")}
        </p>
        <div className="mt-3 flex flex-wrap gap-3">
          <Button variant="outline" asChild data-testid="account-catalog-link">
            <Link to="/producten">
              <Package className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("nav.producten")}
            </Link>
          </Button>
          <Button variant="outline" asChild data-testid="account-orders-link">
            <Link to="/mijn-bestellingen">
              <ClipboardList className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("nav.bestellingen")}
            </Link>
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              logout();
              toast.success(t("account.loggedOut"));
              navigate("/login");
            }}
            data-testid="account-logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("account.logout")}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Account;
